import React from 'react';
import { getRoutes } from 'utils/auth';
import { NotFoundPage } from 'app/components/NotFoundPage';
import { normalizeImpactHubResourcePath } from 'app/pages/ImpactHubPage/utils/viewConfig';

const normalizeResourcePath = (value: string): string =>
  (value || '').trim().split('?')[0].split('#')[0];

/**
 * Checks if a resource exists in user's resource_mappings
 * @param resourceIdentifier - Can be resource name, component name, or path
 * @returns boolean
 */
export const hasResource = (resourceIdentifier: string): boolean => {
  try {
    const resourceMappings = getRoutes() || [];
    if (!Array.isArray(resourceMappings) || resourceMappings.length === 0) {
      return false;
    }

    return resourceMappings.some(resource => {
      const identifier = (resourceIdentifier || '').trim();
      const resourceName = (resource?.name ?? '').trim();
      const resourceComponent = (resource?.component ?? '').trim();
      const resourcePage = (resource?.page ?? '').trim();
      const resourcePath = (resource?.path ?? '').trim();

      if (resourceName === identifier) return true;
      if (resourceComponent === identifier) return true;
      if (resourcePage === identifier) return true;
      if (resourcePath === identifier) return true;

      // Case-insensitive match for feature/component resources (no path).
      if (!identifier.startsWith('/')) {
        const identifierLower = identifier.toLowerCase();
        if (resourceName.toLowerCase() === identifierLower) return true;
        if (resourceComponent.toLowerCase() === identifierLower) return true;
        if (resourcePage.toLowerCase() === identifierLower) return true;
      }

      // View/tab paths (with ?view=) — exact path, Impact Hub legacy ↔ shared, or ImpactHub/<Tab> name.
      if (identifier.includes('?')) {
        const normalizedIdentifier = normalizeImpactHubResourcePath(identifier);
        if (resourcePath.includes('?')) {
          if (normalizeImpactHubResourcePath(resourcePath) === normalizedIdentifier) {
            return true;
          }
        }
        // Match UserManagement-style resource names (e.g. ImpactHub/Pipeline).
        const view = new URLSearchParams(identifier.split('?')[1] || '').get('view');
        if (view && identifier.startsWith('/impact-hub')) {
          const expectedName = `ImpactHub/${view
            .split('-')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join('')}`;
          if (
            resourceName === expectedName ||
            resourcePage === expectedName ||
            resourceComponent === expectedName
          ) {
            return true;
          }
        }
        return false;
      }

      const normalizedIdentifier = normalizeResourcePath(identifier);
      if (!normalizedIdentifier.startsWith('/')) {
        return false;
      }

      const normalizedResourcePath = normalizeResourcePath(resourcePath);
      if (normalizedResourcePath === normalizedIdentifier) {
        return true;
      }

      // Impact Hub page shell: shared /impact-hub (+ legacy ai-avatar path / components).
      const isImpactHubIdentifier =
        normalizedIdentifier === '/impact-hub' ||
        normalizedIdentifier === '/ai-avatar-layout/impact-hub';
      const isImpactHubResourcePath =
        normalizedResourcePath === '/impact-hub' ||
        normalizedResourcePath === '/ai-avatar-layout/impact-hub' ||
        normalizedResourcePath.includes('/impact-hub');
      const isImpactHubComponent =
        resource?.component === 'ImpactHubPage' ||
        resource?.component === 'AIAvatarImpactHubPage' ||
        resource?.component === 'MetricsFrameworkPage' ||
        resource?.component === 'AIAvatarMetricsFrameworkPage';
      if (isImpactHubIdentifier && (isImpactHubResourcePath || isImpactHubComponent)) {
        return true;
      }

      // Autopilot page shell: shared /autopilot (plus legacy measure / ai-avatar autopilot paths).
      const isAutopilotIdentifier =
        normalizedIdentifier === '/autopilot' ||
        normalizedIdentifier === '/ai-avatar-layout/autopilot' ||
        normalizedIdentifier === '/ai-avatar-layout/measure';
      const isAutopilotResourcePath =
        normalizedResourcePath === '/autopilot' ||
        normalizedResourcePath === '/ai-avatar-layout/autopilot' ||
        normalizedResourcePath === '/ai-avatar-layout/measure';
      const isAutopilotComponent =
        resource?.component === 'AutopilotPage' ||
        resource?.component === 'AIAvatarAutopilotPage' ||
        resource?.component === 'AIAvatarMeasurePage' ||
        resource?.component === 'AskMiaAutopilotPage';
      if (isAutopilotIdentifier && (isAutopilotResourcePath || isAutopilotComponent)) {
        return true;
      }

      return false;
    });
  } catch (error) {
    console.error('Error checking resource access:', error);
    return false;
  }
};

/**
 * HOC to protect components based on resource_mappings
 * @param Component - Component to protect
 * @param resourceIdentifier - Resource name/component/path to check
 * @param fallback - Optional fallback component (default: NotFoundPage)
 */
export const withResourceProtection = <P extends object>(
  Component: React.ComponentType<P>,
  resourceIdentifier: string,
  fallback?: React.ReactNode,
) => {
  const ProtectedComponent = React.forwardRef<any, P>((props, ref) => {
    const hasAccess = hasResource(resourceIdentifier);

    if (!hasAccess) {
      const FallbackComponent = fallback !== undefined ? fallback : <NotFoundPage />;
      return FallbackComponent as React.ReactElement;
    }

    if (ref) {
      return <Component {...props} ref={ref} />;
    }

    return <Component {...props} />;
  });

  ProtectedComponent.displayName = `withResourceProtection(${Component.displayName || Component.name || 'Component'})`;

  return ProtectedComponent;
};
